import React, { useMemo, useCallback } from 'react';
import { Tooltip } from '@plerionhq/design-system';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useListConversations } from '../../api/conversations';
import { useDismissSpotlight, type Spotlight } from '../../api/spotlights';
import { Card, CardContent } from '../../dashboard/components/Card';
import { ButtonWithDropdown, DropdownOption } from '../ui/ButtonWithDropdown';
import Badge from '../common/Badge';
import { pleriRouter } from '../../Pleri';
import { useChatStore } from '../Pleri/store/chatStore';

import { useTranslation } from 'react-i18next';

dayjs.extend(relativeTime);

interface SpotlightDetailsProps {
  spotlight: Spotlight;
}

export const SpotlightDetails: React.FC<SpotlightDetailsProps> = ({
  spotlight,
}) => {
  const { t } = useTranslation('pleri/pages');
  // Fetch conversation by batchId (sourceId)
  const { data: conversationsData } = useListConversations(
    spotlight.batchId
      ? { sourceId: spotlight.batchId, isHidden: 'all' }
      : undefined,
    !!spotlight.batchId,
  );

  // Get conversation ID from fetched conversations
  const conversationId = useMemo(() => {
    return conversationsData?.conversations?.[0]?.conversationId;
  }, [conversationsData]);

  const setActiveConversation = useChatStore(
    (state) => state.setActiveConversation,
  );
  const setInputPrompt = useChatStore((state) => state.setInputPrompt);

  const dismissSpotlightMutation = useDismissSpotlight();

  const handleDismiss = useCallback(() => {
    dismissSpotlightMutation.mutate(spotlight.id);
  }, [dismissSpotlightMutation, spotlight.id]);

  // Determine button configuration
  const hasConversation = !!conversationId;
  const isNotDismissed = spotlight.status !== 'dismissed';
  const showButtons = hasConversation || isNotDismissed;

  const dropdownOptions: DropdownOption[] = useMemo(() => {
    const options: DropdownOption[] = [];
    if (hasConversation && isNotDismissed) {
      options.push({
        id: 'dismiss',
        label: t('dismiss'),
        icon: XMarkIcon,
        onClick: handleDismiss,
      });
    }
    return options;
  }, [hasConversation, isNotDismissed, handleDismiss, t]);

  const isDismissing = dismissSpotlightMutation.isPending;

  const mainButtonLabel = hasConversation ? t('investigate') : t('dismiss');
  const mainButtonOnClick = useMemo(() => {
    if (hasConversation) {
      return () => {
        if (conversationId) {
          setActiveConversation({ conversationId });
          pleriRouter.navigate({
            to: '/c/$conversationId',
            params: { conversationId },
          });
          if (spotlight.prompt) {
            setInputPrompt(spotlight.prompt);
          }
        }
      };
    }
    return handleDismiss;
  }, [
    hasConversation,
    conversationId,
    setActiveConversation,
    setInputPrompt,
    spotlight.prompt,
    handleDismiss,
  ]);

  return (
    <Card variant="default" size="md" className="overflow-hidden">
      <CardContent className="px-6">
        {/* Action buttons at the top right */}
        {showButtons && (
          <div className="flex justify-end pb-4">
            <ButtonWithDropdown
              mainButtonLabel={mainButtonLabel}
              mainButtonOnClick={mainButtonOnClick}
              dropdownOptions={dropdownOptions}
              isLoading={isDismissing}
              loadingText={isDismissing ? t('dismissing') : undefined}
            />
          </div>
        )}

        <div className="flex items-start gap-3">
          <div className="flex-1">
            <h3 className="text-base leading-relaxed font-normal text-gray-900 dark:text-gray-100">
              {spotlight.message}
            </h3>
            <div className="flex items-center justify-between pt-6">
              <div className="flex items-center gap-2">
                {spotlight.status === 'dismissed' && (
                  <div className="flex items-center gap-2">
                    <Badge variant="warning" size="sm">
                      {t('dismissed')}
                    </Badge>
                    {spotlight.statusUpdatedBy?.email && (
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {t('by', { email: spotlight.statusUpdatedBy.email })}
                      </span>
                    )}
                  </div>
                )}
              </div>
              <div className="flex flex-col items-end gap-1">
                {spotlight.createdAt && (
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs text-gray-400 dark:text-gray-500">
                      {t('discovered')}
                    </span>
                    <Tooltip
                      label={dayjs(spotlight.createdAt).format(
                        'YY-MM-DD HH:mm:ss',
                      )}
                      placement="top"
                    >
                      <p className="cursor-help text-xs font-medium text-gray-500 dark:text-gray-400">
                        {dayjs(spotlight.createdAt).fromNow()}
                      </p>
                    </Tooltip>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
